'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import AppImage from '@/components/ui/AppImage';
import { Search, MapPin, Clock, Heart, X, Edit2, Trash2, PauseCircle, CheckCircle, Star, Share2, ChevronLeft, Phone, MessageCircle, Flag, Zap, Eye } from 'lucide-react';

interface Product {
  id: string;
  title: string;
  priceSEK: number;
  priceKES: number;
  location: string;
  timeAgo: string;
  condition: string;
  conditionColor: string;
  category: string;
  image: string;
  alt: string;
  seller: string;
  sellerVerified: boolean;
  views: number;
  saved: number;
  status: 'active' | 'sold' | 'on_hold' | 'featured';
  description: string;
  isOwner?: boolean;
}

const SEK_RATE = 13.2; // 1 SEK ≈ 13.2 KES

const initialProducts: Product[] = [
{
  id: 'sw-001', title: 'IKEA KALLAX Shelf Unit White 4x4', priceSEK: 1299, priceKES: Math.round(1299 * SEK_RATE),
  location: 'Stockholm', timeAgo: '2 hrs ago', condition: 'Used', conditionColor: '#FFB300',
  category: 'Furniture', seller: 'Erik L.', sellerVerified: true, views: 340, saved: 28,
  status: 'active', isOwner: false,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ba181bae-1773072255398.png",
  alt: 'White IKEA KALLAX shelf unit with 16 compartments',
  description: 'IKEA KALLAX 4x4 shelf in excellent condition. Only 1 year old. Perfect for books, storage boxes, or display. Dimensions: 147x147cm. Self-pickup from Stockholm Södermalm.'
},
{
  id: 'sw-002', title: 'Volvo V60 2019 Momentum Plus', priceSEK: 285000, priceKES: Math.round(285000 * SEK_RATE),
  location: 'Gothenburg', timeAgo: '5 hrs ago', condition: 'Used', conditionColor: '#FFB300',
  category: 'Cars', seller: 'Anna S.', sellerVerified: true, views: 1240, saved: 89,
  status: 'featured', isOwner: false,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_102a9a99a-1772069885481.png",
  alt: 'Silver Volvo V60 estate car on Swedish road',
  description: 'Volvo V60 2019 Momentum Plus. Full service history. 78,000 km. Heated seats, panoramic roof, Pilot Assist. Swedish-spec car. Priced to sell quickly.'
},
{
  id: 'sw-003', title: 'Husqvarna Automower 430X Robotic', priceSEK: 8500, priceKES: Math.round(8500 * SEK_RATE),
  location: 'Malmö', timeAgo: '1 day ago', condition: 'Like New', conditionColor: '#00C853',
  category: 'Garden', seller: 'Lars B.', sellerVerified: true, views: 560, saved: 45,
  status: 'on_hold', isOwner: true,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b654cd03-1767264423952.png",
  alt: 'Husqvarna robotic lawn mower on green grass',
  description: 'Husqvarna Automower 430X. Used one season. Covers up to 3200 sqm. GPS theft protection. Includes installation kit and boundary wire. Reason for sale: moving to apartment.'
},
{
  id: 'sw-004', title: 'Electrolux Pure Q9 Vacuum Cleaner', priceSEK: 2200, priceKES: Math.round(2200 * SEK_RATE),
  location: 'Uppsala', timeAgo: '3 hrs ago', condition: 'New', conditionColor: '#00C853',
  category: 'Appliances', seller: 'Maria K.', sellerVerified: false, views: 230, saved: 18,
  status: 'active', isOwner: true,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1199adbff-1772277797857.png",
  alt: 'Modern cordless vacuum cleaner on wooden floor',
  description: 'Electrolux Pure Q9 cordless vacuum. Unopened box, bought as gift but already have one. Full warranty. Powerful suction, 60-min battery life.'
},
{
  id: 'sw-005', title: 'Fjällräven Kånken Backpack — Ox Red', priceSEK: 650, priceKES: Math.round(650 * SEK_RATE),
  location: 'Stockholm', timeAgo: '6 hrs ago', condition: 'Used', conditionColor: '#FFB300',
  category: 'Fashion', seller: 'Sofia P.', sellerVerified: false, views: 890, saved: 67,
  status: 'sold', isOwner: false,
  image: "https://images.unsplash.com/photo-1671628031185-8ac6a3b29ed6",
  alt: 'Red Fjällräven Kånken backpack on white background',
  description: 'Classic Fjällräven Kånken in Ox Red. Used for 2 years, still in great shape. No tears or stains. Iconic Swedish design.'
},
{
  id: 'sw-006', title: 'SMEG FAB28 Retro Refrigerator Cream', priceSEK: 9800, priceKES: Math.round(9800 * SEK_RATE),
  location: 'Linköping', timeAgo: '12 hrs ago', condition: 'Used', conditionColor: '#FFB300',
  category: 'Appliances', seller: 'Johan T.', sellerVerified: true, views: 450, saved: 34,
  status: 'active', isOwner: false,
  image: "https://images.unsplash.com/photo-1594656501993-8a30b6b81903",
  alt: 'Cream coloured retro SMEG refrigerator in modern kitchen',
  description: 'SMEG FAB28 retro-style fridge in cream. 3 years old, works perfectly. Moving abroad so must sell. Stunning design piece for any kitchen.'
},
{
  id: 'sw-007', title: 'Specialized Allez Sprint Road Bike', priceSEK: 12500, priceKES: Math.round(12500 * SEK_RATE),
  location: 'Västerås', timeAgo: '2 days ago', condition: 'Used', conditionColor: '#FFB300',
  category: 'Sports', seller: 'Björn A.', sellerVerified: true, views: 780, saved: 56,
  status: 'featured', isOwner: false,
  image: "https://images.unsplash.com/photo-1734454047903-1bb93607a32f",
  alt: 'Red and white Specialized road bike leaning against wall',
  description: 'Specialized Allez Sprint 2021. Size 54cm. Shimano 105 groupset. Carbon fork. 3000km ridden. Serviced last month. Includes pedals and bottle cages.'
},
{
  id: 'sw-008', title: 'Bang & Olufsen Beoplay H9 Headphones', priceSEK: 3200, priceKES: Math.round(3200 * SEK_RATE),
  location: 'Stockholm', timeAgo: '4 hrs ago', condition: 'Like New', conditionColor: '#00C853',
  category: 'Electronics', seller: 'Ingrid M.', sellerVerified: true, views: 1100, saved: 92,
  status: 'active', isOwner: true,
  image: "https://images.unsplash.com/photo-1609090240376-918a3b6c0e00",
  alt: 'Premium over-ear headphones in natural brown leather',
  description: 'B&O Beoplay H9 wireless headphones. Used 6 months. ANC, 25hr battery. Comes with original case and cables. Upgrading to newer model.'
}];


const categories = ['All', 'Cars', 'Electronics', 'Furniture', 'Appliances', 'Fashion', 'Sports', 'Garden'];

const formatSEK = (n: number) => `SEK ${n.toLocaleString('sv-SE')}`;
const formatKES = (n: number) => `KES ${n.toLocaleString('en-KE')}`;

export default function SwedenStorePage() {
  const [products, setProducts] = useState<Product[]>(initialProducts);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [selected, setSelected] = useState<Product | null>(null);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [currency, setCurrency] = useState<'SEK' | 'KES'>('SEK');
  const [saved, setSaved] = useState<Set<string>>(new Set());

  const filtered = products.filter((p) => {
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase());
    const matchCat = activeCategory === 'All' || p.category === activeCategory;
    return matchSearch && matchCat;
  });

  const toggleSave = (id: string) => {
    setSaved((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleDelete = (id: string) => {
    setProducts((prev) => prev.filter((p) => p.id !== id));
    setShowDeleteConfirm(null);
    if (selected?.id === id) setSelected(null);
  };

  const handleStatusChange = (id: string, status: Product['status']) => {
    setProducts((prev) => prev.map((p) => p.id === id ? { ...p, status } : p));
    if (selected?.id === id) setSelected((prev) => prev ? { ...prev, status } : null);
  };

  const openEdit = (p: Product) => {
    setEditProduct(p);
    setEditTitle(p.title);
    setEditPrice(String(p.priceSEK));
    setEditDesc(p.description);
  };

  const saveEdit = () => {
    if (!editProduct) return;
    const newPrice = Number(editPrice) || editProduct.priceSEK;
    setProducts((prev) => prev.map((p) => p.id === editProduct.id ? {
      ...p, title: editTitle, priceSEK: newPrice, priceKES: Math.round(newPrice * SEK_RATE), description: editDesc
    } : p));
    if (selected?.id === editProduct.id) {
      setSelected((prev) => prev ? { ...prev, title: editTitle, priceSEK: newPrice, priceKES: Math.round(newPrice * SEK_RATE), description: editDesc } : null);
    }
    setEditProduct(null);
  };

  const getPrice = (p: Product) => currency === 'SEK' ? formatSEK(p.priceSEK) : formatKES(p.priceKES);

  const statusBadge = (status: Product['status']) => {
    if (status === 'sold') return { label: 'SOLD', bg: '#ef4444', color: '#fff' };
    if (status === 'on_hold') return { label: 'ON HOLD', bg: '#f59e0b', color: '#000' };
    if (status === 'featured') return { label: '⭐ FEATURED', bg: '#7c3aed', color: '#fff' };
    return null;
  };

  return (
    <AppLayout>
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 pb-24">
        {/* Header */}
        <div className="sticky top-0 z-20 py-4 flex flex-col gap-3" style={{ background: 'rgba(10,15,30,0.97)', backdropFilter: 'blur(20px)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🇸🇪</span>
              <div>
                <h1 className="text-xl font-extrabold text-foreground leading-tight">Sweden Store</h1>
                <p className="text-[10px] font-semibold" style={{ color: 'var(--muted-foreground)' }}>Items from Sweden · Priced in SEK</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrency((c) => c === 'SEK' ? 'KES' : 'SEK')}
                className="px-3 py-1.5 rounded-xl text-xs font-bold transition-all active:scale-95"
                style={{ background: 'rgba(0,200,83,0.12)', border: '1px solid var(--primary)', color: 'var(--primary)' }}>
                {currency === 'SEK' ? '🇸🇪 SEK' : '🇰🇪 KES'}
              </button>
            </div>
          </div>
          {/* Search */}
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-2xl" style={{ background: 'var(--input)', border: '1px solid var(--border)' }}>
            <Search size={16} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Sweden listings..."
            className="flex-1 bg-transparent outline-none text-sm font-medium text-foreground placeholder-muted-foreground" />
            {search && <button onClick={() => setSearch('')}><X size={14} style={{ color: 'var(--muted-foreground)' }} /></button>}
          </div>
          {/* Category bar */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {categories.map((cat) =>
            <button key={cat} onClick={() => setActiveCategory(cat)}
            className="flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all active:scale-95"
            style={{
              background: activeCategory === cat ? 'var(--primary)' : 'var(--secondary)',
              color: activeCategory === cat ? '#000' : 'var(--muted-foreground)',
              border: `1px solid ${activeCategory === cat ? 'var(--primary)' : 'var(--border)'}`
            }}>
                {cat}
              </button>
            )}
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex items-center gap-4 py-3 mb-2">
          <span className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>{filtered.length} listings</span>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>·</span>
          <span className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>1 SEK ≈ KES {SEK_RATE}</span>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>·</span>
          <span className="text-xs font-semibold" style={{ color: '#FFB300' }}>🇸🇪 Sweden</span>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {filtered.map((item) => {
            const badge = statusBadge(item.status);
            const isSaved = saved.has(item.id);
            return (
              <div key={item.id} className="listing-card cursor-pointer group" onClick={() => setSelected(item)}>
                <div className="relative">
                  <AppImage src={item.image} alt={item.alt} width={300} height={180} className="w-full h-40 object-cover" />
                  {badge &&
                  <span className="absolute inset-0 flex items-center justify-center text-sm font-extrabold tracking-widest"
                  style={{ background: 'rgba(0,0,0,0.55)', color: badge.color }}>
                      <span className="px-3 py-1 rounded-lg" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
                    </span>
                  }
                  <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                  style={{ background: item.conditionColor, color: '#000' }}>{item.condition}</span>
                  <button className="absolute top-2 right-2 p-1.5 rounded-full transition-all active:scale-90"
                  style={{ background: 'rgba(0,0,0,0.5)' }}
                  onClick={(e) => {e.stopPropagation();toggleSave(item.id);}}>
                    <Heart size={13} color={isSaved ? '#ef4444' : '#fff'} fill={isSaved ? '#ef4444' : 'none'} />
                  </button>
                  {item.status === 'featured' &&
                  <span className="absolute bottom-2 left-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                  style={{ background: '#7c3aed', color: '#fff' }}>⭐ Featured</span>
                  }
                </div>
                <div className="p-3">
                  <p className="text-xs font-semibold text-foreground truncate mb-1">{item.title}</p>
                  <p className="text-sm font-extrabold mb-1" style={{ color: 'var(--primary)' }}>{getPrice(item)}</p>
                  <p className="text-[10px] mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                    {currency === 'SEK' ? `≈ ${formatKES(item.priceKES)}` : `≈ ${formatSEK(item.priceSEK)}`}
                  </p>
                  <div className="flex items-center gap-1 mb-1">
                    <MapPin size={10} style={{ color: 'var(--muted-foreground)' }} />
                    <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{item.location}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Clock size={10} style={{ color: 'var(--muted-foreground)' }} />
                      <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{item.timeAgo}</span>
                    </div>
                    {item.sellerVerified &&
                    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                    style={{ background: 'rgba(0,200,83,0.12)', color: 'var(--primary)' }}>✓ Verified</span>
                    }
                  </div>
                  {/* Owner actions */}
                  {item.isOwner &&
                  <div className="flex items-center gap-1 mt-2 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
                      <button className="flex-1 flex items-center justify-center gap-1 py-1 rounded-lg text-[10px] font-semibold transition-all active:scale-95"
                    style={{ background: 'rgba(59,130,246,0.12)', color: '#60a5fa' }}
                    onClick={(e) => {e.stopPropagation();openEdit(item);}}>
                        <Edit2 size={10} /> Edit
                      </button>
                      <button className="flex-1 flex items-center justify-center gap-1 py-1 rounded-lg text-[10px] font-semibold transition-all active:scale-95"
                    style={{ background: item.status === 'on_hold' ? 'rgba(0,200,83,0.12)' : 'rgba(245,158,11,0.12)', color: item.status === 'on_hold' ? 'var(--primary)' : '#f59e0b' }}
                    onClick={(e) => {e.stopPropagation();handleStatusChange(item.id, item.status === 'on_hold' ? 'active' : 'on_hold');}}>
                        <PauseCircle size={10} /> {item.status === 'on_hold' ? 'Unhold' : 'Hold'}
                      </button>
                      <button className="flex-1 flex items-center justify-center gap-1 py-1 rounded-lg text-[10px] font-semibold transition-all active:scale-95"
                    style={{ background: item.status === 'sold' ? 'rgba(0,200,83,0.12)' : 'rgba(239,68,68,0.12)', color: item.status === 'sold' ? 'var(--primary)' : '#ef4444' }}
                    onClick={(e) => {e.stopPropagation();handleStatusChange(item.id, item.status === 'sold' ? 'active' : 'sold');}}>
                        <CheckCircle size={10} /> {item.status === 'sold' ? 'Unsell' : 'Sold'}
                      </button>
                    </div>
                  }
                </div>
              </div>);

          })}
        </div>

        {filtered.length === 0 &&
        <div className="flex flex-col items-center justify-center py-20 gap-4">
            <span className="text-5xl">🇸🇪</span>
            <p className="text-base font-semibold text-foreground">No listings found</p>
            <p className="text-sm text-center" style={{ color: 'var(--muted-foreground)' }}>Try a different search or category.</p>
          </div>
        }
      </div>

      {/* Product Detail Modal */}
      {selected &&
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
      onClick={() => setSelected(null)}>
          <div className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[92vh] overflow-y-auto"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
        onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <AppImage src={selected.image} alt={selected.alt} width={600} height={320} className="w-full h-56 object-cover" />
              <button className="absolute top-3 left-3 p-2 rounded-xl" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setSelected(null)}>
                <ChevronLeft size={18} color="#fff" />
              </button>
              {statusBadge(selected.status) &&
            <div className="absolute inset-0 flex items-center justify-center">
                  <span className="px-4 py-2 rounded-xl text-lg font-extrabold tracking-widest"
              style={{ background: statusBadge(selected.status)!.bg, color: statusBadge(selected.status)!.color }}>
                    {statusBadge(selected.status)!.label}
                  </span>
                </div>
            }
              <span className="absolute top-3 right-3 px-2 py-1 rounded-full text-[10px] font-bold"
            style={{ background: selected.conditionColor, color: '#000' }}>{selected.condition}</span>
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h2 className="text-lg font-extrabold text-foreground leading-tight flex-1">{selected.title}</h2>
                <button onClick={() => toggleSave(selected.id)}>
                  <Heart size={20} color={saved.has(selected.id) ? '#ef4444' : 'var(--muted-foreground)'} fill={saved.has(selected.id) ? '#ef4444' : 'none'} />
                </button>
              </div>
              <div className="flex items-baseline gap-3 mb-1">
                <span className="text-2xl font-extrabold" style={{ color: 'var(--primary)' }}>{getPrice(selected)}</span>
                <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  ≈ {currency === 'SEK' ? formatKES(selected.priceKES) : formatSEK(selected.priceSEK)}
                </span>
              </div>
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-1"><MapPin size={12} style={{ color: 'var(--muted-foreground)' }} /><span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{selected.location}, Sweden</span></div>
                <div className="flex items-center gap-1"><Clock size={12} style={{ color: 'var(--muted-foreground)' }} /><span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{selected.timeAgo}</span></div>
                <div className="flex items-center gap-1"><Eye size={12} style={{ color: 'var(--muted-foreground)' }} /><span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{selected.views} views</span></div>
              </div>
              <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--muted-foreground)' }}>{selected.description}</p>
              {/* Seller */}
              <div className="flex items-center gap-3 p-3 rounded-2xl mb-4" style={{ background: 'var(--secondary)', border: '1px solid var(--border)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold"
              style={{ background: 'rgba(0,200,83,0.15)', color: 'var(--primary)' }}>
                  {selected.seller[0]}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">{selected.seller}</p>
                  <p className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>🇸🇪 Sweden · {selected.sellerVerified ? '✓ Verified' : 'Unverified'}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Star size={12} fill="#FFB300" color="#FFB300" />
                  <span className="text-xs font-semibold text-foreground">4.8</span>
                </div>
              </div>
              {/* Action buttons */}
              {selected.status !== 'sold' ?
            <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-2 gap-2">
                    <button className="flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-sm transition-all active:scale-95"
                style={{ background: 'var(--primary)', color: '#000' }}>
                      <Phone size={16} /> Call Seller
                    </button>
                    <button className="flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-sm transition-all active:scale-95"
                style={{ background: '#25D366', color: '#fff' }}>
                      <MessageCircle size={16} /> WhatsApp
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button className="flex items-center justify-center gap-2 py-2.5 rounded-2xl font-semibold text-xs transition-all active:scale-95"
                style={{ background: 'var(--secondary)', border: '1px solid var(--border)', color: 'var(--foreground)' }}>
                      <Share2 size={14} /> Share
                    </button>
                    <button className="flex items-center justify-center gap-2 py-2.5 rounded-2xl font-semibold text-xs transition-all active:scale-95"
                style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444' }}>
                      <Flag size={14} /> Report Ad
                    </button>
                  </div>
                  {/* Owner actions in detail */}
                  {selected.isOwner &&
              <div className="mt-2 p-3 rounded-2xl" style={{ background: 'rgba(0,200,83,0.05)', border: '1px solid rgba(0,200,83,0.15)' }}>
                      <p className="text-[10px] font-bold mb-2" style={{ color: 'var(--primary)' }}>YOUR LISTING</p>
                      <div className="grid grid-cols-4 gap-1.5">
                        <button className="flex flex-col items-center gap-1 py-2 rounded-xl text-[10px] font-semibold transition-all active:scale-95"
                  style={{ background: 'rgba(59,130,246,0.12)', color: '#60a5fa' }}
                  onClick={() => {setSelected(null);openEdit(selected);}}>
                          <Edit2 size={14} /> Edit
                        </button>
                        <button className="flex flex-col items-center gap-1 py-2 rounded-xl text-[10px] font-semibold transition-all active:scale-95"
                  style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}
                  onClick={() => handleStatusChange(selected.id, selected.status === 'on_hold' ? 'active' : 'on_hold')}>
                          <PauseCircle size={14} /> {selected.status === 'on_hold' ? 'Unhold' : 'Hold'}
                        </button>
                        <button className="flex flex-col items-center gap-1 py-2 rounded-xl text-[10px] font-semibold transition-all active:scale-95"
                  style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}
                  onClick={() => handleStatusChange(selected.id, selected.status === 'sold' ? 'active' : 'sold')}>
                          <CheckCircle size={14} /> {selected.status === 'sold' ? 'Unsell' : 'Sold'}
                        </button>
                        <button className="flex flex-col items-center gap-1 py-2 rounded-xl text-[10px] font-semibold transition-all active:scale-95"
                  style={{ background: 'rgba(124,58,237,0.12)', color: '#a78bfa' }}
                  onClick={() => handleStatusChange(selected.id, 'featured')}>
                          <Zap size={14} /> Feature
                        </button>
                      </div>
                      <button className="w-full mt-2 flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold transition-all active:scale-95"
                style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}
                onClick={() => {setSelected(null);setShowDeleteConfirm(selected.id);}}>
                        <Trash2 size={13} /> Delete Listing
                      </button>
                    </div>
              }
                </div> :

            <div className="flex items-center justify-center py-4 rounded-2xl" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                  <span className="text-sm font-bold" style={{ color: '#ef4444' }}>This item has been sold</span>
                </div>
            }
            </div>
          </div>
        </div>
      }

      {/* Edit Modal */}
      {editProduct &&
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
      onClick={() => setEditProduct(null)}>
          <div className="w-full sm:max-w-md rounded-3xl p-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
        onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-extrabold text-foreground">Edit Listing</h3>
              <button onClick={() => setEditProduct(null)}><X size={20} style={{ color: 'var(--muted-foreground)' }} /></button>
            </div>
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Title</label>
                <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
              className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none text-foreground"
              style={{ background: 'var(--input)', border: '1px solid var(--border)' }} />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Price (SEK)</label>
                <input type="number" value={editPrice} onChange={(e) => setEditPrice(e.target.value)}
              className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none text-foreground"
              style={{ background: 'var(--input)', border: '1px solid var(--border)' }} />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Description</label>
                <textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} rows={3}
              className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none text-foreground resize-none"
              style={{ background: 'var(--input)', border: '1px solid var(--border)' }} />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setEditProduct(null)} className="flex-1 py-3 rounded-2xl font-semibold text-sm"
              style={{ background: 'var(--secondary)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}>Cancel</button>
                <button onClick={saveEdit} className="flex-1 py-3 rounded-2xl font-semibold text-sm"
              style={{ background: 'var(--primary)', color: '#000' }}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      }

      {/* Delete Confirm */}
      {showDeleteConfirm &&
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
      onClick={() => setShowDeleteConfirm(null)}>
          <div className="w-full max-w-sm rounded-3xl p-6 text-center" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
        onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ background: 'rgba(239,68,68,0.12)' }}>
              <Trash2 size={24} color="#ef4444" />
            </div>
            <h3 className="text-lg font-extrabold text-foreground mb-2">Delete Listing?</h3>
            <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>This action cannot be undone. The listing will be permanently removed.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(null)} className="flex-1 py-3 rounded-2xl font-semibold text-sm"
            style={{ background: 'var(--secondary)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}>Cancel</button>
              <button onClick={() => handleDelete(showDeleteConfirm)} className="flex-1 py-3 rounded-2xl font-semibold text-sm"
            style={{ background: '#ef4444', color: '#fff' }}>Delete</button>
            </div>
          </div>
        </div>
      }
    </AppLayout>);

}